/**
 * The query planner (D520): an UNTRUSTED request body in, a validated {@link LogicalPlan} out (or a stable reason code).
 *
 * What the planner guarantees:
 *  - nothing in the request becomes SQL text: metrics, dimensions and sort fields are looked up BY ID in the catalog (an
 *    unknown or malformed id is a refusal, never an expression), filter values are validated by the dimension's value type
 *    and travel as bound parameters, dates are strict real calendar dates, numbers are bounded integers;
 *  - the source is chosen from the catalog: an hourly aggregate when one carries every requested metric, dimension and
 *    filter, else a fact (single fact table: a metric set that spans two fact tables is refused, `METRICS_SPAN_FACT_TABLES`);
 *  - a currency-bound metric forces the grouping by currency; a ratio is carried as numerator and denominator;
 *  - comparison windows are placed on the calendar (previous period, same period last year by date or by weekday, and the
 *    partial "up to now" cut) — the exact day-by-day pairing happens in the database through `dim_date`.
 *
 * Pure: no I/O and no clock. `asOf` and `today` arrive in the context.
 */
import { AGG_SOURCES, FACT_SOURCES, } from './types.js';
import { COURIER_SENSITIVE_DIMENSIONS, COURIER_SENSITIVE_METRICS, COURIER_SUPPORT_METRIC, SUBJECT_SUPPORT_METRIC, SUPPORT_METRIC, getDimension, getMetric, hasColumn } from './catalog.js';
import { isMerchantReviewed } from './merchant-review.js';
import { FILTER_LIMITS, checkRecordRanges, parseCondition, parseHaving } from './filter.js';
import { addDays, firstOfMonth, firstOfQuarter, firstOfYear, mondayOf, previousQuarter, inclusiveDays, isRealDate, lastOfMonth, lastOfYear, MIN_DATE, previousMonth, shiftYearsClamped, } from './dates.js';
/** The cuts a store-history dimension (new / returning) never meets for a partner: all of them split a month cell back into days or parts of a day. */
const STORE_HISTORY_FORBIDDEN_CUTS = new Set(['day', 'hour', 'weekday', 'day_part', 'holiday', 'shift']);
export const LIMITS = {
    maxMetrics: 12,
    maxDimensions: 4,
    /** All conditions of a request, `filters` and every `anyOf` group together (D528: was 8 conditions, one per dimension). */
    maxFilters: FILTER_LIMITS.maxConditions,
    maxFilterValues: FILTER_LIMITS.maxValues,
    maxAnyOfGroups: FILTER_LIMITS.maxGroups,
    maxHaving: FILTER_LIMITS.maxHaving,
    maxSort: 3,
    defaultLimit: 100,
    maxLimit: 1000,
    maxOffset: 100_000,
    maxPeriodDays: 1100,
    maxHourGrainDays: 32,
};
const refuse = (code, field) => ({ ok: false, code, ...(field !== undefined ? { field } : {}) });
const ID = /^[a-z][a-z0-9_]{0,63}$/;
const REQUEST_KEYS = new Set(['metrics', 'dimensions', 'filters', 'anyOf', 'having', 'period', 'granularity', 'compare', 'compareAgainst', 'sort', 'limit', 'offset', 'totals']);
const SORT_KEYS = new Set(['by', 'direction']);
const RELATIVE = [
    'today', 'yesterday', 'last_7_days', 'last_30_days', 'month_to_date', 'last_month', 'year_to_date', 'last_year',
    'last_week', 'this_week', 'this_quarter', 'last_quarter', 'last_days',
];
const GRANULARITIES = ['total', 'year', 'month', 'day', 'hour'];
const COMPARES = ['previous_period', 'same_period_last_year_by_date', 'same_period_last_year_by_weekday', 'up_to_now'];
const AGAINST = ['previous_period', 'last_year_by_date', 'last_year_by_weekday'];
const isObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
export { validFilterValue } from './filter.js';
// ───────────────────────────── period and comparison ─────────────────────────────
export function resolveRelative(relative, today, days) {
    switch (relative) {
        case 'last_week': {
            const monday = mondayOf(today);
            return { from: addDays(monday, -7), to: addDays(monday, -1) };
        }
        case 'this_week':
            return { from: mondayOf(today), to: today };
        case 'this_quarter':
            return { from: firstOfQuarter(today), to: today };
        case 'last_quarter':
            return previousQuarter(today);
        case 'last_days':
            return { from: addDays(today, -((days ?? 1) - 1)), to: today };
        case 'today':
            return { from: today, to: today };
        case 'yesterday':
            return { from: addDays(today, -1), to: addDays(today, -1) };
        case 'last_7_days':
            return { from: addDays(today, -6), to: today };
        case 'last_30_days':
            return { from: addDays(today, -29), to: today };
        case 'month_to_date':
            return { from: firstOfMonth(today), to: today };
        case 'last_month':
            return previousMonth(today);
        case 'year_to_date':
            return { from: firstOfYear(today), to: today };
        case 'last_year': {
            const lastYear = shiftYearsClamped(today, -1);
            return { from: firstOfYear(lastYear), to: lastOfYear(lastYear) };
        }
    }
}
function resolvePeriod(period, today) {
    if (!isObject(period))
        return refuse('PERIOD_REQUIRED', 'period');
    const keys = Object.keys(period);
    if ('relative' in period) {
        const rel = period.relative;
        if (typeof rel !== 'string' || !RELATIVE.includes(rel))
            return refuse('PERIOD_RELATIVE_UNKNOWN', 'period.relative');
        // `last_days` is the one relative period that carries a number of days (1..1100) beside its name; no other takes a second key.
        if (rel === 'last_days') {
            if (keys.length !== 2 || !('days' in period) || !intIn(period.days, 1, LIMITS.maxPeriodDays))
                return refuse('PERIOD_INVALID', 'period');
            return { ok: true, value: resolveRelative('last_days', today, period.days) };
        }
        if (keys.length !== 1)
            return refuse('PERIOD_INVALID', 'period');
        return { ok: true, value: resolveRelative(rel, today) };
    }
    if (keys.some((k) => k !== 'from' && k !== 'to'))
        return refuse('PERIOD_INVALID', 'period');
    const { from, to } = period;
    if (!isRealDate(from) || !isRealDate(to))
        return refuse('PERIOD_INVALID', 'period');
    if (from > to)
        return refuse('PERIOD_REVERSED', 'period');
    if (inclusiveDays(from, to) > LIMITS.maxPeriodDays)
        return refuse('PERIOD_TOO_LONG', 'period');
    return { ok: true, value: { from, to } };
}
function placeComparison(kind, against, window) {
    const cap = kind === 'up_to_now';
    const how = kind === 'previous_period' ? 'previous_period' : kind === 'same_period_last_year_by_date' ? 'last_year_by_date' : kind === 'same_period_last_year_by_weekday' ? 'last_year_by_weekday' : against;
    if (how === 'previous_period') {
        const len = inclusiveDays(window.from, window.to);
        const from = addDays(window.from, -len);
        if (from < MIN_DATE)
            return refuse('COMPARE_PERIOD_UNPLACEABLE', 'compare');
        return { ok: true, value: { kind, against: how, baselineEnvelope: { from, to: addDays(window.to, -len) }, shiftDays: len, cap } };
    }
    if (how === 'last_year_by_weekday') {
        const from = addDays(window.from, -364);
        if (from < MIN_DATE)
            return refuse('COMPARE_PERIOD_UNPLACEABLE', 'compare');
        return { ok: true, value: { kind, against: how, baselineEnvelope: { from, to: addDays(window.to, -364) }, mapColumn: 'same_weekday_last_year', cap } };
    }
    // By date: the envelope is a day wider on each side so a leap day never falls outside it; the exact pairing is dim_date's.
    const from = addDays(shiftYearsClamped(window.from, -1), -1);
    if (from < MIN_DATE)
        return refuse('COMPARE_PERIOD_UNPLACEABLE', 'compare');
    return { ok: true, value: { kind, against: how, baselineEnvelope: { from, to: addDays(shiftYearsClamped(window.to, -1), 1) }, mapColumn: 'same_date_last_year', cap } };
}
// ───────────────────────────── the planner ─────────────────────────────
const SOURCE_PREFERENCE = [...AGG_SOURCES, ...FACT_SOURCES];
function intIn(value, min, max) {
    return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
}
/** Where a text operator looks: the dimension's label where the source has one, else the code itself (a code is a word, a reference is not). */
function textTarget(def, src) {
    if (src.label !== undefined)
        return 'label';
    return def.valueType === 'code' ? 'key' : undefined;
}
/** Can `source` evaluate the condition? Its dimension is there, a text operator has something to read, a money bound has the row's currency. */
function conditionSupported(c, source) {
    const src = c.def.sources[source];
    if (src === undefined)
        return false;
    if ((c.op === 'contains' || c.op === 'starts_with') && textTarget(c.def, src) === undefined)
        return false;
    if (c.currency !== undefined && !hasColumn(source, 'currency'))
        return false;
    return true;
}
const isPositiveSet = (c) => c.op === 'in';
/** Parse `filters` (AND) and `anyOf` (OR of ANDs): the caps, the operators, the values; two positive sets on one dimension in one list are ambiguous and refused. */
export function parseConditions(filtersRaw, anyOfRaw) {
    const filterInput = filtersRaw ?? [];
    if (!Array.isArray(filterInput))
        return refuse('FILTER_INVALID', 'filters');
    const groupInput = anyOfRaw ?? [];
    if (!Array.isArray(groupInput))
        return refuse('FILTER_GROUP_INVALID', 'anyOf');
    if (groupInput.length > LIMITS.maxAnyOfGroups)
        return refuse('FILTER_TOO_MANY', 'anyOf');
    // An empty group could never be satisfied (and, written as "no condition", would match everything): neither is a meaningful request.
    if (groupInput.some((g) => !Array.isArray(g) || g.length === 0))
        return refuse('FILTER_GROUP_INVALID', 'anyOf');
    let total = filterInput.length;
    for (const g of groupInput)
        total += g.length;
    if (total > LIMITS.maxFilters)
        return refuse('FILTER_TOO_MANY', 'filters');
    const parseList = (list) => {
        const out = [];
        for (const raw of list) {
            const one = parseCondition(raw);
            if (!one.ok)
                return one;
            // Two positive sets on one dimension in one list: intersect or unite? Neither is asked for; say so (a range or an exclusion is fine).
            if (isPositiveSet(one.value) && out.some((x) => x.def === one.value.def && isPositiveSet(x)))
                return refuse('FILTER_DUPLICATED', one.value.def.id);
            out.push(one.value);
        }
        return { ok: true, value: out };
    };
    const top = parseList(filterInput);
    if (!top.ok)
        return top;
    const groups = [];
    for (const g of groupInput) {
        const one = parseList(g);
        if (!one.ok)
            return one;
        groups.push(one.value);
    }
    const narrow = checkRecordRanges(top.value, groups);
    if (narrow !== undefined)
        return narrow;
    return { ok: true, value: { top: top.value, groups } };
}
export function planQuery(raw, ctx) {
    if (!isObject(raw))
        return refuse('REQUEST_INVALID');
    const unknownKey = Object.keys(raw).find((k) => !REQUEST_KEYS.has(k));
    if (unknownKey !== undefined)
        return refuse('REQUEST_FIELD_UNKNOWN');
    const req = raw;
    const audience = ctx.audience ?? 'internal';
    const maxLimit = ctx.maxLimit ?? LIMITS.maxLimit;
    // ---- metrics ----
    const support = ctx.support === true || ctx.supportOnly === true;
    if (!Array.isArray(req.metrics) || (req.metrics.length === 0 && ctx.supportOnly !== true) || (ctx.supportOnly === true && req.metrics.length !== 0))
        return refuse('METRICS_REQUIRED', 'metrics');
    if (req.metrics.length > LIMITS.maxMetrics)
        return refuse('METRICS_TOO_MANY', 'metrics');
    const metricDefs = [];
    for (const id of req.metrics) {
        if (typeof id !== 'string' || !ID.test(id))
            return refuse('METRIC_UNKNOWN', 'metrics');
        const def = getMetric(id);
        if (def === undefined)
            return refuse('METRIC_UNKNOWN', id);
        if (metricDefs.includes(def))
            return refuse('METRIC_DUPLICATED', id);
        const admitted = audience === 'partner' && ctx.reviewedFor === 'merchant' && def.adminOnly === true && !def.internalOnly && isMerchantReviewed('metric', id);
        if ((def.internalOnly || def.adminOnly === true) && audience !== 'internal' && !admitted)
            return refuse('METRIC_INTERNAL_ONLY', id);
        metricDefs.push(def);
    }
    // ---- granularity and dimensions ----
    const granularity = (req.granularity ?? 'total');
    if (typeof granularity !== 'string' || !GRANULARITIES.includes(granularity))
        return refuse('GRANULARITY_UNKNOWN', 'granularity');
    const dimInput = req.dimensions ?? [];
    if (!Array.isArray(dimInput))
        return refuse('DIMENSION_UNKNOWN', 'dimensions');
    const dimDefs = [];
    for (const id of dimInput) {
        if (typeof id !== 'string' || !ID.test(id))
            return refuse('DIMENSION_UNKNOWN', 'dimensions');
        const def = getDimension(id);
        if (def === undefined)
            return refuse('DIMENSION_UNKNOWN', id);
        if (dimDefs.includes(def))
            return refuse('DIMENSION_DUPLICATED', id);
        // A record-level number (an order's own distance, a posting's own amount) is filtered on, never grouped by: one group per record.
        if (def.filterOnly === true)
            return refuse('DIMENSION_FILTER_ONLY', id);
        // D531: the dimensions of the new analyses are the platform admin's until D533 reviews them for a partner.
        if (def.adminOnly === true && audience !== 'internal' && !(audience === 'partner' && ctx.reviewedFor === 'merchant' && isMerchantReviewed('dimension', id)))
            return refuse('DIMENSION_UNKNOWN', id);
        dimDefs.push(def);
    }
    if (granularity !== 'total') {
        const timeDim = getDimension(granularity === 'year' ? 'year' : granularity === 'month' ? 'month' : granularity === 'day' ? 'day' : 'hour');
        const chain = dimDefs.filter((d) => d.hierarchy?.chain === 'time');
        if (chain.some((d) => d !== timeDim))
            return refuse('GRANULARITY_CONFLICT', 'granularity');
        if (!dimDefs.includes(timeDim))
            dimDefs.unshift(timeDim);
    }
    if (dimDefs.length > LIMITS.maxDimensions)
        return refuse('DIMENSIONS_TOO_MANY', 'dimensions');
    // ---- period and comparison ----
    const period = resolvePeriod(req.period, ctx.today);
    if (!period.ok)
        return period;
    const window = period.value;
    if (dimDefs.some((d) => d.id === 'hour') && inclusiveDays(window.from, window.to) > LIMITS.maxHourGrainDays)
        return refuse('HOUR_GRAIN_RANGE_TOO_LONG', 'period');
    let compare;
    if (req.compare !== undefined) {
        if (typeof req.compare !== 'string' || !COMPARES.includes(req.compare))
            return refuse('COMPARE_UNKNOWN', 'compare');
        const kind = req.compare;
        let against = 'last_year_by_date';
        if (req.compareAgainst !== undefined) {
            if (kind !== 'up_to_now' || typeof req.compareAgainst !== 'string' || !AGAINST.includes(req.compareAgainst))
                return refuse('COMPARE_BASELINE_INVALID', 'compareAgainst');
            against = req.compareAgainst;
        }
        const placed = placeComparison(kind, against, window);
        if (!placed.ok)
            return placed;
        compare = placed.value;
    }
    else if (req.compareAgainst !== undefined) {
        return refuse('COMPARE_BASELINE_INVALID', 'compareAgainst');
    }
    // ---- filters (D528: conditions of several operators, several per dimension, one level of OR groups) ----
    const parsed = parseConditions(req.filters, req.anyOf);
    if (!parsed.ok)
        return parsed;
    const { top: topConditions, groups: groupConditions } = parsed.value;
    if (audience !== 'internal') {
        const admin = [...topConditions, ...groupConditions.flat()].find((c) => c.def.adminOnly === true && !(audience === 'partner' && ctx.reviewedFor === 'merchant' && isMerchantReviewed('dimension', c.def.id)));
        if (admin !== undefined)
            return refuse('DIMENSION_UNKNOWN', admin.def.id);
    }
    // D533: "new or returning" is measured against the first day of the ROW's own calendar month (never against the request's window), so a caller cannot
    // move the line by moving the window and difference two answers into the first-timers of one day. What keeps that true is structural, not a convention
    // of the portal's: a partner or merchant reading it gets MONTHLY cells over WHOLE calendar months, with no cut by time of day or weekday (a month cell
    // narrowed to one weekday is the day-level cell again), and no baseline window (it would need its own start).
    const usesStoreHistory = (d) => Object.values(d.sources).some((src) => src?.key.kind === 'merchantCustomerType');
    const storeHistoryUsers = [...dimDefs, ...topConditions.map((c) => c.def), ...groupConditions.flat().map((c) => c.def)].filter(usesStoreHistory);
    if (compare !== undefined && storeHistoryUsers.length > 0)
        return refuse('COMPARE_NOT_AVAILABLE_FOR_DIMENSION', storeHistoryUsers[0].id);
    if (audience !== 'internal' && storeHistoryUsers.length > 0) {
        if (!dimDefs.some((d) => d.id === 'month') || dimDefs.some((d) => d.hierarchy?.chain === 'time' && d.id !== 'month'))
            return refuse('STORE_HISTORY_MONTHLY_ONLY', 'granularity');
        const cut = [...dimDefs, ...topConditions.map((c) => c.def), ...groupConditions.flat().map((c) => c.def)].find((d) => STORE_HISTORY_FORBIDDEN_CUTS.has(d.id));
        if (cut !== undefined)
            return refuse('STORE_HISTORY_MONTHLY_ONLY', cut.id);
        if (window.from !== firstOfMonth(window.from) || window.to !== lastOfMonth(window.to))
            return refuse('STORE_HISTORY_WHOLE_MONTHS', 'period');
    }
    // ---- limit, offset, totals ----
    const limit = req.limit === undefined ? LIMITS.defaultLimit : req.limit;
    if (!intIn(limit, 1, maxLimit))
        return refuse('LIMIT_INVALID', 'limit');
    const offset = req.offset === undefined ? 0 : req.offset;
    if (!intIn(offset, 0, LIMITS.maxOffset))
        return refuse('OFFSET_INVALID', 'offset');
    if (req.totals !== undefined && typeof req.totals !== 'boolean')
        return refuse('REQUEST_INVALID', 'totals');
    // ---- having (conditions on the value of a requested metric) ----
    const havingInput = req.having ?? [];
    if (!Array.isArray(havingInput) || havingInput.length > LIMITS.maxHaving)
        return refuse('HAVING_INVALID', 'having');
    const parsedHaving = [];
    for (const h of havingInput) {
        const one = parseHaving(h, metricDefs);
        if (!one.ok)
            return one;
        parsedHaving.push(one.value);
    }
    // The hidden support metric rides along on the partner path; it never counts as one the request asked for.
    // D533: with `subjectsOptional`, a request that NO customer-carrying source can answer is planned without the people support (its plan says `peopleBasis: 'none'`).
    const subjectSources = Object.keys(SUBJECT_SUPPORT_METRIC.formulas);
    const noPeopleBasis = ctx.subjects === true &&
        ctx.subjectsOptional === true &&
        (ctx.forceSource !== undefined ? !subjectSources.includes(ctx.forceSource) : !subjectSources.some((s) => metricDefs.every((m) => m.formulas[s] !== undefined)));
    const withSubjects = ctx.subjects === true && !noPeopleBasis;
    // D533 review P3: a time that contains the courier's trip puts a floor of distinct couriers on the answer (partner path only).
    const withCouriers = support && ctx.subjects === true &&
        (ctx.couriers === true ||
            metricDefs.some((m) => COURIER_SENSITIVE_METRICS.has(m.id)) ||
            [...dimDefs, ...topConditions.map((c) => c.def), ...groupConditions.flat().map((c) => c.def)].some((d) => COURIER_SENSITIVE_DIMENSIONS.has(d.id)));
    const planMetricDefs = support ? [...metricDefs, SUPPORT_METRIC, ...(withSubjects ? [SUBJECT_SUPPORT_METRIC] : []), ...(withCouriers ? [COURIER_SUPPORT_METRIC] : [])] : metricDefs;
    // ---- source selection ----
    const allConditions = [...topConditions, ...groupConditions.flat()];
    const sourceHasAll = (source) => planMetricDefs.every((m) => m.formulas[source] !== undefined);
    const metricSources = SOURCE_PREFERENCE.filter(sourceHasAll);
    if (metricSources.length === 0)
        return refuse('METRICS_SPAN_FACT_TABLES', 'metrics');
    const supports = (source) => dimDefs.every((d) => d.sources[source] !== undefined) && allConditions.every((c) => conditionSupported(c, source));
    const source = metricSources.find((s) => (ctx.forceSource === undefined || s === ctx.forceSource) && supports(s));
    if (source === undefined) {
        const lonelyDim = dimDefs.find((d) => !metricSources.some((s) => d.sources[s] !== undefined));
        if (lonelyDim !== undefined)
            return refuse('DIMENSION_NOT_AVAILABLE_FOR_METRICS', lonelyDim.id);
        const lonelyFilter = allConditions.find((c) => !metricSources.some((s) => conditionSupported(c, s)));
        if (lonelyFilter !== undefined)
            return refuse('FILTER_NOT_AVAILABLE_FOR_METRICS', lonelyFilter.def.id);
        return refuse('DIMENSIONS_NOT_COMBINABLE');
    }
    const dims = dimDefs.map((def, index) => ({ index, def, src: def.sources[source] }));
    const metrics = planMetricDefs.map((def, index) => ({ index, def, formula: def.formulas[source] }));
    const plannedOf = (c) => {
        const src = c.def.sources[source];
        return { def: c.def, src, op: c.op, values: c.values, ...(c.currency !== undefined ? { currency: c.currency } : {}), target: textTarget(c.def, src) === 'label' && (c.op === 'contains' || c.op === 'starts_with') ? 'label' : 'key' };
    };
    const filters = topConditions.map(plannedOf);
    const anyOf = groupConditions.map((g) => g.map(plannedOf));
    const having = parsedHaving.map((h) => ({ metric: metrics.find((m) => m.def === h.metric), op: h.op, values: h.values, ...(h.currency !== undefined ? { currency: h.currency } : {}) }));
    const currencyKey = metrics.some((m) => m.def.currencyBound) || dims.some((d) => d.def.id === 'currency');
    if (currencyKey && !hasColumn(source, 'currency'))
        return refuse('DIMENSIONS_NOT_COMBINABLE', 'currency');
    const marketKey = dims.some((d) => d.src.marketScoped === true || d.def.id === 'market');
    // ---- sort ----
    const sortInput = req.sort ?? [];
    if (!Array.isArray(sortInput) || sortInput.length > LIMITS.maxSort)
        return refuse('SORT_INVALID', 'sort');
    const sort = [];
    for (const s of sortInput) {
        if (!isObject(s) || Object.keys(s).some((k) => !SORT_KEYS.has(k)))
            return refuse('SORT_INVALID', 'sort');
        if (typeof s.by !== 'string' || !ID.test(s.by))
            return refuse('SORT_UNKNOWN_FIELD', 'sort');
        const direction = s.direction === undefined ? undefined : s.direction;
        if (direction !== undefined && direction !== 'asc' && direction !== 'desc')
            return refuse('SORT_INVALID', 'sort');
        const metric = metrics.find((m) => m.def.id === s.by);
        const dimension = dims.find((d) => d.def.id === s.by);
        if (metric === undefined && dimension === undefined)
            return refuse('SORT_UNKNOWN_FIELD', s.by);
        if (sort.some((x) => x.id === s.by))
            return refuse('SORT_INVALID', s.by);
        sort.push({ kind: metric !== undefined ? 'metric' : 'dimension', id: s.by, direction: direction ?? (metric !== undefined ? 'desc' : 'asc') });
    }
    // No explicit sort: a time series reads oldest-first, any other grouping reads best-first by the first metric.
    const timeDims = dims.filter((d) => d.def.kind === 'time');
    const effectiveSort = sort.length > 0 || dims.length === 0
        ? sort
        : timeDims.length > 0
            ? timeDims.map((d) => ({ kind: 'dimension', id: d.def.id, direction: 'asc' }))
            : [{ kind: 'metric', id: metrics[0].def.id, direction: 'desc' }];
    return {
        ok: true,
        value: {
            source,
            path: source.startsWith('agg_') ? 'agg' : 'fact',
            window,
            ...(compare !== undefined ? { compare } : {}),
            asOf: ctx.asOf,
            granularity,
            dims,
            marketKey,
            currencyKey,
            metrics,
            filters,
            anyOf,
            having,
            ...(support ? { supportIndex: metrics.findIndex((m) => m.def === SUPPORT_METRIC) } : {}),
            ...(support && withSubjects ? { subjectIndex: metrics.findIndex((m) => m.def === SUBJECT_SUPPORT_METRIC) } : {}),
            ...(withCouriers ? { courierIndex: metrics.findIndex((m) => m.def === COURIER_SUPPORT_METRIC) } : {}),
            ...(support && noPeopleBasis ? { peopleBasis: 'none' } : {}),
            ...(ctx.fetchAll !== undefined ? { page: { limit, offset } } : {}),
            sort: effectiveSort,
            limit: ctx.fetchAll ?? limit,
            offset: ctx.fetchAll !== undefined ? 0 : offset,
            totals: req.totals === true,
            // A record-level number is an ENTITY grain (D528 review P1-1): beside a distinct-people count it is the linkage probe, so the plan is personal
            // (the personal word, the personal role, the access log) exactly as a person or an order would make it.
            personal: dims.some((d) => d.def.personal) || allConditions.some((c) => c.def.personal) || (allConditions.some((c) => c.def.recordLevel === true) && metrics.some((m) => m.def.internalOnly)),
            audience,
        },
    };
}
//# sourceMappingURL=plan.js.map